import { useSyncExternalStore } from 'react';

/**
 * A11Y-MOTION-INLINE-STYLES — reads the user's `prefers-reduced-motion`
 * setting and subscribes to changes in it.
 *
 * Why this exists
 * ───────────────
 * Most of our motion lives in CSS (tokens.css) and is already gated
 * behind `@media (prefers-reduced-motion: reduce)`. But a handful of
 * surfaces animate via inline `style={{ transition: '...' }}` — drawer
 * slides, scrim fade-ins, the global search overlay backdrop — and
 * those bypass the CSS media query entirely. This hook is the
 * one-liner that lets those components opt in to the same a11y
 * contract without a refactor:
 *
 *   const reducedMotion = usePrefersReducedMotion();
 *   ...
 *   style={{
 *     transition: reducedMotion
 *       ? 'none'
 *       : 'transform var(--hm-dur-slow) var(--hm-ease)',
 *   }}
 *
 * The hook returns `true` when the user has explicitly asked the OS to
 * reduce motion (System Settings → Accessibility → Reduce Motion on
 * macOS / Windows / iOS / Android, or the equivalent on Linux). The
 * default — and the value during SSR or in environments where
 * `matchMedia` isn't available — is `false`, matching the legacy
 * "animate everything" behaviour.
 *
 * Implementation notes
 * ────────────────────
 * - Uses `useSyncExternalStore` so React 19 concurrent renders stay in
 *   sync with the OS-level setting even if the user toggles it mid-
 *   session. We don't fire a re-render in a useEffect because that
 *   would lag one paint behind the change.
 * - Listens via `addEventListener('change', …)` rather than the
 *   deprecated `addListener` — Safari ≥14 and every other evergreen
 *   browser support the new API.
 * - SSR-safe: the server snapshot returns `false`. There's no flash of
 *   animated content on hydration because the inline-style components
 *   that consume this hook only run on the client (we don't SSR drawer
 *   chrome).
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

const MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mql = window.matchMedia(MEDIA_QUERY);
  // Modern browsers (Safari ≥14, every Chromium, every Firefox) ship
  // EventTarget on MediaQueryList. The fallback to addListener exists
  // for older WebKit (e.g. iOS 13) — we keep it defensive but expect
  // the new path on every supported runtime.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', callback);
    return () => mql.removeEventListener('change', callback);
  }
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  mql.addListener(callback);
  return () => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    mql.removeListener(callback);
  };
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(MEDIA_QUERY).matches;
}

function getServerSnapshot(): boolean {
  return false;
}
